// Enhanced Data Service with Offline Support
// Wraps the main data service to add offline queueing and sync
import { dataApi } from './baseDataService';
import { offlineStorage } from './offlineStorage';
import { syncService } from './syncService';

// Rows created offline need a local id until the server issues a real one.
// Real ids are small serial integers, so staying above this floor keeps the two
// kinds impossible to confuse, and the counter avoids two sales made in the same
// millisecond sharing an id.
const TEMP_ID_FLOOR = 1_000_000_000_000;
let tempIdCounter = 0;
const nextTempId = (): number => TEMP_ID_FLOOR + Date.now() % TEMP_ID_FLOOR + tempIdCounter++;
const isTempId = (id: number): boolean => Number(id) >= TEMP_ID_FLOOR;

// Enhanced API with offline support
export const offlineDataApi = {
  // Get inventory - try online first, fallback to offline
  getInventory: async (stallId?: number) => {
    try {
      if (navigator.onLine) {
        const result = await dataApi.getInventory(stallId);
        // Save to offline storage
        if (result.items) {
          for (const item of result.items) {
            await offlineStorage.saveItem(item);
          }
        }

        // Refresh the cached allocations too. They only used to be written when
        // this device did the distributing, so a stall whose stock was sent from
        // another phone kept working off old allocations once it went offline
        // and could show — and sell — stock it no longer had.
        try {
          const { distributions } = await dataApi.getDistributions();
          for (const dist of distributions || []) {
            await offlineStorage.saveDistribution(dist);
          }
        } catch (cacheError) {
          console.warn('[OfflineDataApi] Could not refresh cached allocations:', cacheError);
        }

        return result;
      } else {
        // Offline: get from IndexedDB and calculate stock from distributions
        console.log(`[OfflineDataApi] Getting inventory from offline storage for stall: ${stallId || 'admin'}`);
        const items = await offlineStorage.getItems();
        const distributions = await offlineStorage.getDistributions();
        const sales = await offlineStorage.getSales();

        // If stallId is provided (non-admin user), calculate stock from distributions
        if (stallId !== undefined) {
          console.log(`[OfflineDataApi] Filtering for stall ${stallId}, found ${distributions.length} distributions`);

          const filteredItems = items
            .map(item => {
              // Get all distributions to this stall for this item
              const stallDistributions = distributions.filter(
                d => d.item_id === item.item_id && d.stall_id === stallId
              );

              const totalDistributedToStall = stallDistributions.reduce(
                (sum, d) => sum + d.quantity_allocated, 0
              );

              // If no stock distributed to this stall, exclude this item
              if (totalDistributedToStall === 0) {
                return null;
              }

              // Calculate sales from this stall for this item
              const stallSales = sales.filter(
                s => s.item_id === item.item_id && s.stall_id === stallId
              );
              const totalSold = stallSales.reduce((sum, s) => sum + s.quantity_sold, 0);

              // Calculate stock available for this stall
              const currentStock = Math.max(0, totalDistributedToStall - totalSold);

              return {
                ...item,
                current_stock: currentStock,
                total_allocated: totalDistributedToStall,
                stall_id: stallId
              };
            })
            .filter((item): item is any => item !== null);

          console.log(`[OfflineDataApi] Returning ${filteredItems.length} items for stall ${stallId}`);
          return { items: filteredItems };
        }

        // Admin: return all items with admin stock calculation
        // Admin offline: use cached current_stock from the last online sync (already
        // computed with replay). Stall/distribution/sales rows adjust stall views only.
        const itemsWithAdminStock = items.map(item => {
          const totalDistributed = distributions
            .filter(d => d.item_id === item.item_id)
            .reduce((sum, d) => sum + d.quantity_allocated, 0);

          return {
            ...item,
            current_stock: item.current_stock ?? 0,
            total_allocated: totalDistributed
          };
        });

        return { items: itemsWithAdminStock };
      }
    } catch (error) {
      console.error('[OfflineDataApi] Error getting inventory:', error);
      // The online read failed. Serve the last synced copy so the shop can keep
      // trading, but flag it as stale so the screen can say the numbers may be
      // behind. With nothing cached there is nothing honest to show: an empty
      // list would read as "every item is gone", so surface the error instead.
      const items = await offlineStorage.getItems();
      if (items.length === 0) throw error;
      return { items, stale: true };
    }
  },

  // Create item - queue if offline
  createItem: async (itemData: any) => {
    try {
      if (navigator.onLine) {
        const result = await dataApi.createItem(itemData);
        await offlineStorage.saveItem(result.item);
        return result;
      } else {
        // Offline: save to IndexedDB and queue for sync
        console.log('[OfflineDataApi] Creating item offline, queuing for sync');
        const tempId = Date.now();
        const offlineItem = { ...itemData, item_id: tempId, date_added: new Date().toISOString() };
        await offlineStorage.saveItem(offlineItem);
        await syncService.queueOperation('CREATE', 'items', { ...itemData, __tempItemId: tempId });
        return { item: offlineItem };
      }
    } catch (error) {
      console.error('[OfflineDataApi] Error creating item:', error);
      throw error;
    }
  },

  // Add stock (delta-based) - queue if offline
  addStock: async (itemId: number, quantity: number) => {
    try {
      if (navigator.onLine) {
        const result = await dataApi.addStock(itemId, quantity);
        if (result?.item) {
          await offlineStorage.saveItem(result.item);
        }
        return result;
      } else {
        // Offline: optimistically bump local copy and queue the DELTA so it
        // can never be shrunk by stale totals when it syncs later.
        console.log('[OfflineDataApi] Adding stock offline, queuing for sync');
        const items = await offlineStorage.getItems();
        const existingItem: any = items.find((i: any) => i.item_id === itemId) || { item_id: itemId };
        const updatedItem = {
          ...existingItem,
          total_added: (existingItem.total_added || 0) + quantity,
          current_stock: (existingItem.current_stock || 0) + quantity
        };
        await offlineStorage.saveItem(updatedItem);
        await syncService.queueOperation('CREATE', 'stock_additions', { item_id: itemId, quantity });
        return { item: updatedItem };
      }
    } catch (error) {
      console.error('[OfflineDataApi] Error adding stock:', error);
      throw error;
    }
  },

  // Update item - queue if offline
  updateItem: async (itemId: number, itemData: any) => {
    try {
      if (navigator.onLine) {
        const result = await dataApi.updateItem(itemId, itemData);
        await offlineStorage.saveItem(result.item);
        return result;
      } else {
        // Offline: update in IndexedDB and queue for sync
        console.log('[OfflineDataApi] Updating item offline, queuing for sync');

        // Retrieve existing item to ensure we don't lose data (partial update fix)
        const items = await offlineStorage.getItems();
        const existingItem = items.find((i: any) => i.item_id === itemId) || {};

        const updatedItem = {
          ...existingItem,
          ...itemData,
          item_id: itemId
        };

        // Cache the merged row for display, but queue only the fields the user
        // actually edited. Sending the whole cached row replayed stale values
        // over newer server ones, and because offline "add stock" bumps the
        // cached total_added, it also made the server bank that stock a second
        // time on top of the queued addition.
        await offlineStorage.saveItem(updatedItem);
        await syncService.queueOperation('UPDATE', 'items', { ...itemData, item_id: itemId });
        return { item: updatedItem };
      }
    } catch (error) {
      console.error('[OfflineDataApi] Error updating item:', error);
      throw error;
    }
  },

  // Create sale - queue if offline
  createSale: async (saleData: any) => {
    try {
      if (navigator.onLine) {
        const result = await dataApi.createSale(saleData);
        await offlineStorage.saveSale(result.sale);
        return result;
      } else {
        // Offline: save to IndexedDB and queue for sync
        console.log('[OfflineDataApi] Creating sale offline, queuing for sync');
        const tempId = nextTempId();
        const offlineSale = {
          ...saleData,
          sale_id: tempId,
          date_time: new Date().toISOString()
        };
        await offlineStorage.saveSale(offlineSale);
        // Carry the placeholder id so the sync can clear this local row once the
        // server issues a real one; otherwise the device counts the sale twice.
        await syncService.queueOperation('CREATE', 'sales', { ...saleData, __tempSaleId: tempId });
        return { sale: offlineSale };
      }
    } catch (error) {
      console.error('[OfflineDataApi] Error creating sale:', error);
      throw error;
    }
  },

  // Update sale - queue if offline
  updateSale: async (saleId: number, saleData: any) => {
    try {
      if (navigator.onLine) {
        const result = await dataApi.updateSale(saleId, saleData);
        await offlineStorage.saveSale(result.sale);
        return result;
      } else {
        // Offline: update in IndexedDB and queue for sync
        console.log('[OfflineDataApi] Updating sale offline, queuing for sync');

        // A sale made offline has no server id yet, so an update queued against
        // its placeholder id would never match a row and would retry forever.
        if (isTempId(saleId)) {
          throw new Error(
            'This sale hasn\'t reached the server yet. Delete it and record it again, or reconnect first.'
          );
        }

        // Retrieve existing sale to ensure we don't lose data
        const styles = await offlineStorage.getSales();
        const existingSale = styles.find((s: any) => s.sale_id === saleId) || {};

        const updatedSale = {
          ...existingSale,
          ...saleData,
          sale_id: saleId
        };

        // As with items: cache the merged row for display, but queue only the
        // edited fields so stale cached values can't overwrite newer ones.
        await offlineStorage.saveSale(updatedSale);
        await syncService.queueOperation('UPDATE', 'sales', { ...saleData, sale_id: saleId });
        return { sale: updatedSale };
      }
    } catch (error) {
      console.error('[OfflineDataApi] Error updating sale:', error);
      throw error;
    }
  },

  getSalesAggregates: async () => {
    const buildFromSales = (sales: any[]) => {
      const byItem: Record<number, number> = {};
      const byItemStall: Record<string, number> = {};
      for (const sale of sales) {
        const itemId = Number(sale.item_id);
        const qty = Number(sale.quantity_sold) || 0;
        byItem[itemId] = (byItem[itemId] || 0) + qty;
        const stallKey = `${itemId}-${sale.stall_id ?? 'null'}`;
        byItemStall[stallKey] = (byItemStall[stallKey] || 0) + qty;
      }
      return { byItem, byItemStall };
    };

    try {
      if (navigator.onLine) {
        return await dataApi.getSalesAggregates();
      }
      const sales = await offlineStorage.getSales();
      return buildFromSales(sales);
    } catch (error) {
      console.error('[OfflineDataApi] Error getting sales aggregates:', error);
      // These totals are the "sold" column and the withdraw limit. Empty totals
      // would silently overstate what is still available, so refuse rather than
      // guess when there is no cached sales history to fall back on.
      const sales = await offlineStorage.getSales();
      if (sales.length === 0) throw error;
      return buildFromSales(sales);
    }
  },

  // Get sales - try online first, fallback to offline
  getSales: async () => {
    try {
      if (navigator.onLine) {
        const result = await dataApi.getSales();
        // Save to offline storage
        if (result.sales) {
          for (const sale of result.sales) {
            await offlineStorage.saveSale(sale);
          }
        }
        return result;
      } else {
        // Offline: get from IndexedDB
        console.log('[OfflineDataApi] Getting sales from offline storage');
        const sales = await offlineStorage.getSales();
        return { sales };
      }
    } catch (error) {
      console.error('[OfflineDataApi] Error getting sales:', error);
      const sales = await offlineStorage.getSales();
      if (sales.length === 0) throw error;
      return { sales, stale: true };
    }
  },

  // Distribute stock - handle offline
  distributeStock: async (distributionData: {
    item_id: number;
    distributions: Array<{ stall_id: number; quantity: number }>;
    notes?: string;
  }) => {
    try {
      if (navigator.onLine) {
        const result = await dataApi.distributeStock(distributionData);

        // Save distributions to offline storage for offline access
        if (result && result.distributions) {
          for (const dist of result.distributions) {
            await offlineStorage.saveDistribution(dist);
          }
        }

        // Also refetch and cache the updated inventory
        const inventory = await dataApi.getInventory();
        if (inventory.items) {
          for (const item of inventory.items) {
            await offlineStorage.saveItem(item);
          }
        }

        return result;
      } else {
        // Distributing offline used to report success and write placeholder
        // rows, but nothing ever sent them to the server, so the allocation was
        // lost while the device showed it as done. Moving stock between the hub
        // and the stalls has to be confirmed by the server, so ask for a
        // connection rather than accepting work we cannot keep.
        throw new Error(
          'Distributing stock needs an internet connection so the stall totals stay correct on every device. Reconnect and try again.'
        );
      }
    } catch (error) {
      console.error('[OfflineDataApi] Error distributing stock:', error);
      throw error;
    }
  },

  // Create withdrawal - queue if offline
  createWithdrawal: async (withdrawalData: any) => {
    try {
      if (navigator.onLine) {
        const result = await dataApi.createWithdrawal(withdrawalData);
        // Only cache locally if we have a valid withdrawal_id — IndexedDB
        // requires it as the keyPath and will throw if it's null/undefined.
        if (result && result.withdrawal && result.withdrawal.withdrawal_id != null) {
          await offlineStorage.saveWithdrawal(result.withdrawal);
        }
        return result;
      } else {
        throw new Error(
          'Withdrawing stock from the central hub needs an internet connection so every device stays consistent. Reconnect and try again.'
        );
      }
    } catch (error) {
      console.error('[OfflineDataApi] Error creating withdrawal:', error);
      throw error;
    }
  },

  // Other methods pass through to main API
  getUsers: dataApi.getUsers,
  createUser: dataApi.createUser,
  updateUser: dataApi.updateUser,
  deleteUser: dataApi.deleteUser,
  getStalls: dataApi.getStalls,
  createStall: dataApi.createStall,
  updateStall: dataApi.updateStall,
  deleteStall: dataApi.deleteStall,
  deleteItem: dataApi.deleteItem,
  getDistributions: dataApi.getDistributions,
  updateDistribution: dataApi.updateDistribution,
  deleteDistribution: dataApi.deleteDistribution,

  // Delete sale - queue if offline
  deleteSale: async (saleId: number) => {
    try {
      if (navigator.onLine) {
        const result = await dataApi.deleteSale(saleId);
        await offlineStorage.deleteSale(saleId);
        return result;
      } else {
        // Offline: delete from IndexedDB and queue for sync
        console.log('[OfflineDataApi] Deleting sale offline, queuing for sync');
        await offlineStorage.deleteSale(saleId);
        await syncService.cancelOrQueueSaleDeletion([saleId]);
        return { success: true };
      }
    } catch (error) {
      console.error('[OfflineDataApi] Error deleting sale:', error);
      throw error;
    }
  },

  // Bulk delete sales - queue if offline
  bulkDeleteSales: async (saleIds: number[]) => {
    try {
      if (navigator.onLine) {
        const result = await dataApi.bulkDeleteSales(saleIds);
        await offlineStorage.bulkDeleteSales(saleIds);
        return result;
      } else {
        // Offline: delete from IndexedDB and queue for sync
        console.log('[OfflineDataApi] Bulk deleting sales offline, queuing for sync');
        await offlineStorage.bulkDeleteSales(saleIds);
        await syncService.cancelOrQueueSaleDeletion(saleIds);
        return { success: true };
      }
    } catch (error) {
      console.error('[OfflineDataApi] Error bulk deleting sales:', error);
      throw error;
    }
  },

  getStockAdditions: dataApi.getStockAdditions,

  deleteStockAddition: async (additionId: number) => {
    try {
      if (navigator.onLine) {
        return await dataApi.deleteStockAddition(additionId);
      } else {
        // For simplicity, stock addition cleanup is only supported online for now
        // as it involves complex recalculations of item totals
        throw new Error('Stock addition cleanup requires an internet connection.');
      }
    } catch (error) {
      console.error('[OfflineDataApi] Error deleting stock addition:', error);
      throw error;
    }
  },

  getStockWithdrawals: dataApi.getStockWithdrawals,

  deleteStockWithdrawal: async (withdrawalId: number) => {
    try {
      if (navigator.onLine) {
        return await dataApi.deleteStockWithdrawal(withdrawalId);
      } else {
        throw new Error('Stock withdrawal deletion requires an internet connection.');
      }
    } catch (error) {
      console.error('[OfflineDataApi] Error deleting stock withdrawal:', error);
      throw error;
    }
  },

  // Withdraw from distribution - queue if offline
  withdrawFromDistribution: async (distributionId: number, quantityToWithdraw: number) => {
    try {
      if (navigator.onLine) {
        const result = await dataApi.withdrawFromDistribution(distributionId, quantityToWithdraw);
        // Don't pre-fetch inventory here — the component calls fetchItems() after
        // the modal closes, which will get the fresh value from Supabase.
        return result;
      } else {
        // This used to report a confident success while nothing was ever sent
        // to the server, so the stock came back on the screen but not in the
        // records. A withdrawal has to be checked against the live batch size,
        // which is only knowable online.
        throw new Error(
          'Withdrawing stock needs an internet connection so it can be checked against the stall\'s current balance. Reconnect and try again.'
        );
      }
    } catch (error) {
      console.error('[OfflineDataApi] Error withdrawing from distribution:', error);
      throw error;
    }
  },

  withdrawFromStall: async (params: {
    item_id: number;
    stall_id: number;
    quantity: number;
    reason?: string;
    notes?: string;
  }) => {
    try {
      if (navigator.onLine) {
        return await (dataApi as any).withdrawFromStall(params);
      }
      throw new Error('Stall withdrawal requires an internet connection.');
    } catch (error) {
      console.error('[OfflineDataApi] Error withdrawing from stall:', error);
      throw error;
    }
  }
};



export default offlineDataApi;
